import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getRoutes } from '../../services/routeService';
import { logout } from './authSlice';
import { RouteData, PaginationMetadata } from '../../types/route';

interface RoutesState {
  routes: RouteData[];
  loading: boolean;
  error: string | null;
  searchParams: {
    shipmentNumber: string;
    serialNumber: string;
  };
  pagination: PaginationMetadata;
}

const initialState: RoutesState = {
  routes: [],
  loading: false,
  error: null,
  searchParams: {
    shipmentNumber: '',
    serialNumber: '',
  },
  pagination: {
    currentPage: 0,
    totalPages: 0,
    totalItems: 0,
    pageSize: 50,
    hasNext: false,
    hasPrevious: false,
  },
};

// Async thunk for fetching routes
export const fetchRoutes = createAsyncThunk(
  'routes/fetchRoutes',
  async (params: { 
    shipmentNumber: string; 
    serialNumber?: string;
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
  }, { getState }) => {
    const state = getState() as any;
    const token = state.auth.token;
    const response = await getRoutes(params, token);
    return response;
  }
);

const routesSlice = createSlice({
  name: 'routes',
  initialState,
  reducers: {
    setSearchParams: (state, action: PayloadAction<{ shipmentNumber?: string; serialNumber?: string }>) => {
      state.searchParams = {
        shipmentNumber: action.payload.shipmentNumber || '',
        serialNumber: action.payload.serialNumber || ''
      };
      // Reset to first page when search params change
      state.pagination.currentPage = 0;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    clearRoutes: (state) => {
      state.routes = [];
      state.error = null;
      state.pagination = {
        currentPage: 0,
        totalPages: 0,
        totalItems: 0,
        pageSize: 50,
        hasNext: false,
        hasPrevious: false,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoutes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoutes.fulfilled, (state, action) => {
        state.loading = false;
        // Check if response is paginated or legacy format
        if (action.payload && typeof action.payload === 'object' && 'content' in action.payload) {
          // Paginated response
          state.routes = action.payload.content;
          state.pagination = action.payload.pagination;
        } else {
          // Legacy response (array of routes)
          state.routes = action.payload as RouteData[];
          state.pagination = {
            currentPage: 0,
            totalPages: 1,
            totalItems: (action.payload as RouteData[]).length,
            pageSize: 50,
            hasNext: false,
            hasPrevious: false,
          };
        }
        state.error = null;
      })
      .addCase(fetchRoutes.rejected, (state, action) => {
        state.loading = false;
        state.routes = [];
        state.error = action.error.message || 'Failed to fetch routes';
      })
      // Clear routes data when user logs out
      .addCase(logout, (state) => {
        state.routes = [];
        state.error = null;
        state.searchParams = {
          shipmentNumber: '',
          serialNumber: '',
        };
        state.pagination = {
          currentPage: 0,
          totalPages: 0,
          totalItems: 0,
          pageSize: 50,
          hasNext: false,
          hasPrevious: false,
        };
      });
  },
});

export const { setSearchParams, setCurrentPage, clearRoutes, clearError } = routesSlice.actions;
export default routesSlice.reducer; 